import { IS_BAD_NETWORK_DETECTED } from './browser';
import { parseHtml } from './parsing';
import { getUniqueId } from './random';

export function makeDraggable(sliderEl: HTMLElement, allowVerticalScroll = false) {
  let mouseDown = false,
    isDragging = false;
  let timer: NodeJS.Timeout = null;
  let startX: number, scrollLeft: number;

  sliderEl.addEventListener('click', (e: MouseEvent) => {
    if (!isDragging || e.target === sliderEl) return;
    e.stopPropagation();
    e.preventDefault();
  });

  const startDragging = (e: MouseEvent) => {
    e.preventDefault();
    mouseDown = true;
    startX = e.pageX - sliderEl.offsetLeft;
    scrollLeft = sliderEl.scrollLeft;
  };

  const stopDragging = () => {
    mouseDown = false;
    clearTimeout(timer);
    timer = setTimeout(() => (isDragging = false), 100);
  };

  sliderEl.addEventListener('mousemove', (e) => {
    e.preventDefault();
    if (!mouseDown) {
      return;
    }

    const x = e.pageX - sliderEl.offsetLeft;
    const scroll = x - startX;
    sliderEl.scrollLeft = scrollLeft - scroll;
    setTimeout(() => (isDragging = true), 100);
  });

  if (!allowVerticalScroll) {
    sliderEl.addEventListener('wheel', (e) => {
      e.preventDefault();
      sliderEl.scrollLeft += e.deltaY;
    });
  }

  // Add the event listeners
  sliderEl.addEventListener('mousedown', startDragging);
  sliderEl.addEventListener('mouseup', stopDragging);
  sliderEl.addEventListener('mouseleave', stopDragging);
}

export function decodeHtml(html = '') {
  if (!html) return '';
  const dom = parseHtml(`<!doctype html><body>${html}`);
  return dom.textContent;
}

export function stripHtml(html = '') {
  if (!html) return '';
  const doc = parseHtml(`<div>${html}`);
  return doc.innerText ?? doc.textContent ?? '';
}

export const generateTemplate = (template: string) => (params?: Record<string, any>) =>
  params ? template.replace(/<%=(.*?)%>/g, (match, key) => params[key?.trim()] ?? match ?? '') : template;

export const domIsReady = () =>
  new Promise<void>((resolve) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        resolve();
      });
    } else {
      resolve();
    }
  });

const timeouts = new Map<string, NodeJS.Timeout>();

/**
 * Observe added DOM element and call callback when the elements are in the viewport
 * @param selector - the selector to observe
 * @param callback - the callback to call when the elements are in the viewport
 * @param removeCallback - the callback to call when the elements are removed
 * @param options.includeHidden - include hidden elements in the viewport
 * @param options.intersectionObserverOptions - the intersection observer options
 * @param options.delay - the delay before calling the callback
 */
export const watchInviewNodes = (
  selector: string,
  callback: (node: HTMLElement) => void,
  removeCallback?: (element: HTMLElement) => unknown,
  options?: {
    includeHidden?: boolean;
    intersectionObserverOptions?: IntersectionObserverInit;
    delay?: number;
  },
) => {
  const { includeHidden, intersectionObserverOptions, delay } = {
    includeHidden: false,
    intersectionObserverOptions: { rootMargin: IS_BAD_NETWORK_DETECTED ? '0% 0%' : '25% 25%' },
    ...options,
  };

  const observer = new IntersectionObserver((intersectedEls) => {
    intersectedEls.forEach((e) => {
      const element = e.target as HTMLElement;
      const intersecting =
        e.isIntersecting ||
        (includeHidden && e.boundingClientRect.height === 0 && e.boundingClientRect.width === 0) ||
        element.dataset.forceInview === 'true';

      element.dataset.inviewId ||= getUniqueId('inview');

      if (intersecting) {
        const cb = () => {
          observer.unobserve(e.target);
          callback(element);
        };

        delay ? timeouts.set(element.dataset.inviewId, setTimeout(cb, delay)) : cb();
      } else {
        clearTimeout(timeouts.get(element.dataset.inviewId));
      }
    });
  }, intersectionObserverOptions);

  const onNodeRemoved = (node: HTMLElement) => {
    removeCallback?.(node);
    observer.unobserve(node);
    clearTimeout(timeouts.get(node.dataset.inviewId));
  };

  watchAddedNodes(selector, observer.observe.bind(observer), onNodeRemoved);

  return observer;
};

const filterNodes = (nodes: NodeList, selector: string) => {
  const filteredNodes = Array.from(nodes)
    .filter((node) => node.nodeType === 1)
    .map((node) => node as HTMLElement);
  const selfNodes = filteredNodes.filter((node) => node.matches(selector));
  const innerNodes = filteredNodes
    .filter((node) => node.querySelector<HTMLElement>(selector))
    .flatMap((node) => Array.from(node.querySelectorAll<HTMLElement>(selector)));
  return [...selfNodes, ...innerNodes];
};

/**
 * Observe DOM mutations
 * @param selector - Filter the elements matchin or including elements matching.
 * @param callback - Callback
 * @param removeCallback - Callback for removed elements
 * @param immediate - Launch callback on al document matching elements at start
 * @param options - MutationObserver Options
 * @returns MutationObserver instance
 */
export const watchAddedNodes = async (
  selector: string,
  callback: (element: HTMLElement) => unknown,
  removeCallback?: (element: HTMLElement) => unknown,
  immediate = true,
  options: MutationObserverInit = {
    childList: true,
    subtree: true,
  },
) => {
  if (!callback || typeof callback !== 'function') throw new Error('callback must be a function');
  if (!selector) throw new Error('querySelector must have a value');
  await domIsReady();

  if (immediate) {
    const initialElements = Array.from(document.querySelectorAll<HTMLElement>(selector));
    initialElements.forEach((el) => callback(el));
  }

  const observer = new MutationObserver((mutationsList) => {
    if (removeCallback) {
      const removedElements = mutationsList
        .filter((mutation) => mutation.type === 'childList' && mutation.removedNodes.length)
        .flatMap(({ removedNodes }) => filterNodes(removedNodes, selector));

      removedElements.filter((el) => el.dataset.nodeId).forEach((el) => removeCallback(el));
    }

    const addedElements = mutationsList
      .filter((mutation) => mutation.type === 'childList' && mutation.addedNodes.length)
      .flatMap(({ addedNodes }) => filterNodes(addedNodes, selector));

    addedElements.forEach((el) => {
      el.dataset.nodeId ||= getUniqueId('node');
      callback(el);
    });
  });
  observer.observe(document, options);
  return observer;
};
